import { csvFormat } from 'd3-dsv';
import { chain, Dictionary, keyBy, keys, sortBy, uniq } from 'lodash';
import { AssayInfo, GetGroup } from './assays';
import { buildSortIdxByGene } from './export-utils';
import {
  getFlagForNormalisationMode,
  getNormalisedValue,
  isDetectedForNormalisationMode,
} from './normalisation-mode';
import { getRobustBioNumber, hasDuplicateBioNumbers } from './sampling-points';
import { CsvRow, CsvRows, FullSamplesByUID, NormalisationMode } from './types';
import { countDetectedGenesPerTarget, flattenRelevantAbundances, flattenSamplesByUID } from './utils';

const HEATMAP_CSV_BASE_COLUMNS = ['group', 'gene', 'assay'] as const;

export function getHeatmapCsvExportContent(
  samplesByUID: FullSamplesByUID,
  normalisationMode: NormalisationMode,
  getAssayInfo: (assay: string) => AssayInfo | undefined,
): string {
  const { rows, columns } = getCsvAssayRowsWithSamplesAsColumns(samplesByUID, normalisationMode, getAssayInfo);
  return csvFormat(rows, columns);
}

function getCsvAssayRowsWithSamplesAsColumns(
  samplesByUID: FullSamplesByUID,
  normalisationMode: NormalisationMode,
  getAssayInfo: (assay: string) => AssayInfo | undefined,
): { rows: CsvRows; columns: string[] } {
  const samples = flattenSamplesByUID(samplesByUID);
  const abundanceBySampleIdAy = chain(samples)
    .keyBy(s => s.id)
    .mapValues(s => keyBy(s.abundances, a => a.assay))
    .value();
  const allAssayIds = samples.flatMap(s => s.abundances.map(r => r.assay));
  const assays = uniq(allAssayIds);
  if (allAssayIds.length !== assays.length * samples.length) {
    throw Error('All assays not analysed for all samples');
  }

  const hasDuplicateBioNums = hasDuplicateBioNumbers(samplesByUID);
  const sampleColumns = uniq(
    samples.flatMap(sample => {
      const bioRepNumber = getRobustBioNumber(sample, hasDuplicateBioNums);
      return [bioRepNumber, `${bioRepNumber}_FLAG`];
    }),
  );
  const unsortedRows = assays.reduce<CsvRows>((assayRowsAcc, assay) => {
    const ayInfo = getAssayInfo(assay);
    if (!ayInfo) {
      throw Error(`Unknown gene ${assay}`);
    }
    const group = ayInfo.l2Target;
    const assayRow = samples.reduce<CsvRow>(
      (rowColsAcc, sample) => {
        const abundance = abundanceBySampleIdAy[sample.id][assay];
        const bioRepNumber = getRobustBioNumber(sample, hasDuplicateBioNums);
        const value = getNormalisedValue(abundance, normalisationMode);
        rowColsAcc[bioRepNumber] = value
          ? normalisationMode === NormalisationMode.SIXTEEN_S
            ? value
            : value.toFixed(0)
          : undefined;
        const modeFlag = getFlagForNormalisationMode(abundance, normalisationMode);
        rowColsAcc[`${bioRepNumber}_FLAG`] = modeFlag ?? '';
        return rowColsAcc;
      },
      { group, gene: ayInfo.gene, assay },
    );
    return [...assayRowsAcc, assayRow];
  }, []);

  const minimalAdequateGetGroup = (ay: string, _?: string) => getAssayInfo(ay)?.l2Target;
  const idxByGene = buildSortIdxByGene(samples, 'l2Target', minimalAdequateGetGroup);
  return {
    rows: sortBy(unsortedRows, row => (row.gene ? idxByGene[row.gene] : 0)),
    columns: [...HEATMAP_CSV_BASE_COLUMNS, ...sampleColumns],
  };
}

export function getNumberOfDetectedGenesByBioNumCsv(
  samplesByBioKey: FullSamplesByUID,
  getGroup: GetGroup,
  normalisationMode: NormalisationMode,
): string {
  const sampleNumberColumn = 'Sample';
  const numDetectedColumn = 'Total number of detected genes';
  const descriptionColumn = 'Description';

  const samples = flattenSamplesByUID(samplesByBioKey);
  const hasDuplicateNums = hasDuplicateBioNumbers(samplesByBioKey);
  const rows = samples.map(sample => {
    const perTargetCounts = countDetectedGenesPerTarget([sample], getGroup, normalisationMode);
    const sampleNum = getRobustBioNumber(sample, hasDuplicateNums);
    return keys(perTargetCounts).reduce<Dictionary<string | number>>(
      (acc, target) => {
        acc[target] = perTargetCounts[target];
        return acc;
      },
      {
        [sampleNumberColumn]: sampleNum,
        [descriptionColumn]: sample.samplingPoint.name,
        [numDetectedColumn]: uniq(
          flattenRelevantAbundances([sample]).filter(datum => isDetectedForNormalisationMode(datum, normalisationMode)).map(datum => datum.assay),
        ).length,
      },
    );
  });
  return csvFormat(rows);
}
